import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import toast from "components/shared/toast/toast";
import { PROJECT, RECURRING, ACCESS } from "services/apiEndpoints";
import { privateDelete, privateGet, privatePut } from "services/privateRequest";
import { makeQueryString } from "utils/utils";

const initialState = {
  projects: [],
  totalProjects: 0,
  selectedProjects: null,
  projectsModalOpen: false,
  isFetching: false,
  allProjects: [],
  filterValues: {},
  projectsL: {
    list: [],
    totalCount: 0,
    pageNumber: 0,
    limit: 10,
    status: "idle", // 'idle' | 'loading' | 'error'
    filters: {
      selectedTeam: { label: "", value: "" },
      selectedStatus: "all",
      selectedCreatedBy: "all",
    },
  },
  recurringsL: {
    list: [],
    totalCount: 0,
    pageNumber: 0,
    limit: 10,
    status: "idle", // 'idle' | 'loading' | 'error'
    filters: {
      selectedTeam: { label: "", value: "" },
      selectedStatus: "all",
      selectedCreatedBy: "all",
    },
  },
  createdbyList: [],
  selectedProjectId: {},
};

// Projects
export const getProjectsData = createAsyncThunk(
  "projects/getProjectsData",
  async (params) => {
    const res = await privateGet(PROJECT, { params });
    // console.log("res :>> ", res);
    return {
      data: res?.data?.data?.projects || [],
      totalProjects: res?.data?.length || 0,
    };
  }
);

export const getAllProjectsData = createAsyncThunk(
  "projects/getAllProjectsData",
  async () => {
    const res = await privateGet(PROJECT);
    return {
      data: res?.data?.data || [],
    };
  }
);

export const deleteProjects = createAsyncThunk(
  "projects/deleteProjects",
  async (id, { dispatch }) => {
    await privateDelete(PROJECT, id);
    dispatch(getProjectsData());
    toast.success("Projects deleted successfully");
  }
);

export const archiveProject = createAsyncThunk(
  "projects/archiveProject",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      await privatePut(RECURRING, id, payload);
      toast.success("Projects Archived successfully");
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const activeProject = createAsyncThunk(
  "projects/activeProject",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      await privatePut(RECURRING, id, payload);
      toast.success("Projects Actived successfully");
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const fetchProjectList = createAsyncThunk(
  "projects/fetchProjectList",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      if (payload.createdBy === "All") {
        payload.createdBy = ""
      }
      const queryString = makeQueryString(PROJECT, payload);
      const response = await privateGet(queryString);
      dispatch(
        setProjectsPagination({
          pageNumber: payload.pageNumber,
          limit: payload.limit,
        })
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching form data:", error);
      rejectWithValue(error);
    }
  }
);

// Recurring
export const fetchRecurring = createAsyncThunk(
  "projects/fetchRecurring",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      if (payload.createdBy === "All") {
        payload.createdBy = ""
      }
      const state = getState()
      const selectedProjectId = state.projects.selectedProjectId
      const payloadData = Object.keys(selectedProjectId).length === 0 ? payload : selectedProjectId
      const queryString = makeQueryString(RECURRING, payloadData);
      const response = await privateGet(queryString);
      dispatch(
        setProjectsPagination({
          pageNumber: payload.pageNumber,
          limit: payload.limit,
        })
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching form data:", error);
      rejectWithValue(error);
    }
  }
);


// access

export const fetchRoleAccess = createAsyncThunk(
  "projects/fetchRoleAccess",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await privateGet(ACCESS);
      return [{ label: "All", value: "All" }, ...response.data.data
        .filter((val) => val.name !== "Submitter")
        .map((val) => ({ label: val.name, value: val._id }))];
    } catch (error) {
      console.error("Error fetching form data:", error);
      rejectWithValue(error);
    }
  }
);

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    setIsFetching: (state, action) => {
      state.isFetching = action.payload;
    },
    setSelectedProjects: (state, action) => {
      state.selectedProjects = action.payload;
      state.projectsModalOpen = !!action.payload;
    },
    setProjectsModalOpen: (state, action) => {
      state.projectsModalOpen = action.payload;
    },
    setFilterValues: (state, action) => {
      state.filterValues = action.payload;
    },
    setSelectedTeamFilter: (state, action) => {
      if (!action.payload)
        state.projectsL.filters.selectedTeam = { label: "", value: "" };
      else state.projectsL.filters.selectedTeam = action.payload;
    },
    setSelectedStatusFilter: (state, action) => {
      state.projectsL.filters.selectedStatus = action.payload;
    },
    setSelectedCreatedByFilter: (state, action) => {
      state.projectsL.filters.selectedCreatedBy = action.payload;
    },
    setProjectsPagination: (state, action) => {
      state.projectsL.pageNumber = action.payload.pageNumber;
      state.projectsL.limit = action.payload.limit;
    },
    clearProjects: (state) => {
      state.projectsL = { ...initialState.projectsL };
    },
    setSelectedProjectID: (state, action) => {
      // console.log("action", action.payload)
      state.selectedProjectId = action.payload
    }
  },
  extraReducers: {
    [getProjectsData.pending]: (state) => {
      state.isFetching = true;
    },
    [getProjectsData.fulfilled]: (state, action) => {
      state.projects = action.payload.data;
      state.totalProjects = action.payload.totalProjects;
      state.isFetching = false;
    },
    [getProjectsData.rejected]: (state) => {
      state.isFetching = false;
    },
    [getAllProjectsData.fulfilled]: (state, action) => {
      state.allProjects = action.payload.data;
    },
    [fetchProjectList.pending]: (state) => {
      state.projectsL.status = "loading";
    },
    [fetchProjectList.fulfilled]: (state, action) => {
      state.projectsL.status = "idle";
      state.projectsL.list = action?.payload?.projects || [];
      state.projectsL.totalCount = action?.payload?.totalProjects || 0;
    },
    [fetchProjectList.rejected]: (state) => {
      state.projectsL.status = "error";
    },
    //recurring
    [fetchRecurring.pending]: (state) => {
      state.recurringsL.status = "loading";
    },
    [fetchRecurring.fulfilled]: (state, action) => {
      state.recurringsL.status = "idle";
      state.recurringsL.list = action?.payload?.recurrings || [];
      state.recurringsL.totalCount = action?.payload?.totalRecurrings || 0;
    },
    [fetchRecurring.rejected]: (state) => {
      state.recurringsL.status = "error";
    },


    /// access
    [fetchRoleAccess.pending]: (state) => {
      state.recurringsL.status = "loading";
    },
    [fetchRoleAccess.fulfilled]: (state, action) => {
      // console.log(action.payload);
      state.recurringsL.status = "idle";
      state.createdbyList = action?.payload || [];
    },
    [fetchRoleAccess.rejected]: (state) => {
      state.recurringsL.status = "error";
    },
  },
});

export default projectsSlice.reducer;
export const {
  setIsFetching,
  setSelectedProjects,
  setProjectsModalOpen,
  setFilterValues,
  setProjectsPagination,
  setSelectedTeamFilter,
  setSelectedStatusFilter,
  setSelectedCreatedByFilter,
  clearProjects,
  setSelectedProjectID,
} = projectsSlice.actions;
